import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../hooks/redux";
import { API_URL } from "../../../../redux/reduxQuery/api";
import {
  ExportRequest,
  RequestSearchResponse,
  TemplateResponse,
  ExcelJSWorkbookInternal,
  ExcelJSWorksheetInternal,
  ExcelJSRowInternal,
} from "../../../../types/atoms/exportRequestTypes";

export const useActionButtons = (queryString: string = "", search?: string) => {
  const [openFilters, setOpenFilters] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const globalUserFilter = useAppSelector((state) => state.filter.request);
  const token = useAppSelector((s) => s.auth.token);
  let globalUserFilterLength = 0;
  if (Object.keys(globalUserFilter ?? {})?.length) {
    globalUserFilterLength = Object.keys(globalUserFilter ?? {}).length;
  }
  const navigate = useNavigate();
  const cleanedQs = queryString?.startsWith("&")
    ? queryString.slice(1)
    : queryString || "";
  const finalQs =
    cleanedQs +
    (search
      ? `${cleanedQs ? "&" : ""}searchParam=${encodeURIComponent(search)}`
      : "");
  const filterButton = {
    label: `Filters${
      globalUserFilterLength ? ` (${globalUserFilterLength})` : ""
    }`,
    icon: "AdjustmentsVerticalIcon",
    base: "secondary",
    onClick: () => setOpenFilters(true),
    btnContainerClasses: "box-border border-2 border-primary-400 border-solid",
  };

  const actionButtonsConfig = [
    {
      label: isExporting ? "Exporting..." : "Export CSV",
      icon: "ArrowDownTrayIcon",
      disabled: isExporting,
      loader: isExporting,
      onClick: async () => {
        setIsExporting(true);
        try {
          const headers = new Headers();
          if (token) headers.set("authorization", `Bearer ${token}`);
          const pageSize = 500;
          let skip = 0;
          const allRows: ExportRequest[] = [];
          while (true) {
            const searchUrl = `${API_URL}/request-search?${
              finalQs ? finalQs + "&" : ""
            }take=${pageSize}&skip=${skip}`;
            const resp = await fetch(searchUrl, { headers, cache: "no-store" });
            if (!resp.ok)
              throw new Error(`Fetch data failed: ${resp.statusText}`);
            const json: RequestSearchResponse = await resp.json();
            const { requests = [] } = json || {};
            if (!requests.length) break;
            allRows.push(...requests);
            skip += requests.length;
            if (requests.length < pageSize) break;
          }

          const normalizeEmail = (s?: string) => {
            const first = String(s || "")
              .split(/[;,]/)[0]
              .trim()
              .toLowerCase();
            return first;
          };
          allRows.sort((a, b) => {
            const ea = normalizeEmail(a?.requesterEmails);
            const eb = normalizeEmail(b?.requesterEmails);
            if (ea < eb) return -1;
            if (ea > eb) return 1;
            const ad = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bd = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
            return bd - ad;
          });

          const tplUrl = `${API_URL}/requests/template?base64=1&_t=${Date.now()}`;
          const tplResp = await fetch(tplUrl, { headers, cache: "no-store" });
          if (!tplResp.ok)
            throw new Error(
              `Template fetch failed: ${tplResp.status} ${tplResp.statusText}`
            );
          const tplJson: TemplateResponse = await tplResp.json();
          if (!tplJson?.base64) throw new Error("Template base64 missing");
          const tplBuffer = Uint8Array.from(atob(tplJson.base64), (c) =>
            c.charCodeAt(0)
          ).buffer;

          const ExcelJS = (await import("exceljs")).default;
          const workbook = new ExcelJS.Workbook();
          let worksheet;
          try {
            await workbook.xlsx.load(tplBuffer);
            worksheet = workbook.getWorksheet(1);
          } catch (e) {
            console.warn(
              "Template load failed, falling back to new workbook. Reason:",
              e
            );
            const wb2 = new ExcelJS.Workbook();
            worksheet = wb2.addWorksheet("Requests");
            worksheet.getCell("A1").value = "Frazer - Outstanding Request";
            const header = [
              "Requestor Email",
              "Patient Name",
              "Client ID",
              "Provider",
              "Date(s) of Service",
              "Date Submitted to RayNa",
              "Rayna Notes",
              "Close by Date if not Received **Can be Reopened at any Time**",
            ];
            worksheet.addRow([]); // empty row 2
            worksheet.addRow(header); // row 3
            // Replace workbook with fallback workbook
            // ExcelJS internal properties not exposed in types
            (workbook as unknown as ExcelJSWorkbookInternal)._worksheets = (
              wb2 as unknown as ExcelJSWorkbookInternal
            )._worksheets;
            (workbook as unknown as ExcelJSWorkbookInternal)._worksheetHash = (
              wb2 as unknown as ExcelJSWorkbookInternal
            )._worksheetHash;
          }

          if (!worksheet) {
            throw new Error("Could not read worksheet from template");
          }

          const ensurePatientHeaderLabel = () => {
            const rowsToInspect = [1, 2, 3, 4, 5];
            rowsToInspect.forEach((rowNumber) => {
              const row = worksheet.getRow(rowNumber);
              if (!row) return;
              row.eachCell((cell: { value?: unknown }) => {
                if (
                  typeof cell.value === "string" &&
                  cell.value.trim().toLowerCase() === "client name"
                ) {
                  cell.value = "Patient Name";
                }
              });
            });
          };

          ensurePatientHeaderLabel();

          const stripTime = (date: Date) =>
            new Date(date.getFullYear(), date.getMonth(), date.getDate());

          const toDateOrEmpty = (date?: string | Date): Date | "" => {
            if (!date) return "";
            const d = new Date(date);
            if (isNaN(d.getTime())) return "";
            return stripTime(d);
          };

          const formatServiceDateValue = (
            value?: string | Date
          ): string | Date => {
            if (!value) return "";
            if (value instanceof Date) {
              return stripTime(value);
            }
            const raw = String(value).trim();
            if (!raw) return "";
            const looksLikeRange =
              raw.includes(" - ") ||
              raw.includes(" to ") ||
              raw.includes("\n") ||
              raw.toLowerCase().includes("present");
            const parsed = new Date(raw);
            if (!Number.isNaN(parsed.getTime()) && !looksLikeRange) {
              return stripTime(parsed);
            }
            return raw;
          };

          const computeCloseByDate = (date?: string | Date): Date | "" => {
            if (!date) return "";
            const d = new Date(date);
            if (isNaN(d.getTime())) return "";
            const result = new Date(d);
            result.setDate(result.getDate() + 60);
            return stripTime(result);
          };

          const firstDataRow = 4;
          const referenceRow = worksheet.getRow(firstDataRow);
          const referenceHeight =
            referenceRow?.height || worksheet.properties.defaultRowHeight || 15;
          const desiredRowHeight = referenceHeight * 4; // each row = height of 4 rows
          allRows.forEach((r, idx) => {
            const rowIndex = firstDataRow + idx;
            const row = worksheet.getRow(rowIndex); // start at row 4
            const clientId = (r.patient?.caseId as string) || "";

            const serviceDate = formatServiceDateValue(r.serviceDates);
            const submittedDate = toDateOrEmpty(r.submittedDate);
            const closeBy = computeCloseByDate(r.submittedDate);

            const values: (string | Date)[] = [
              r.requesterEmails || "",
              r.patient?.name || "",
              clientId,
              r.provider?.name || "",
              serviceDate,
              submittedDate,
              r.requestNotes || "",
              closeBy,
            ];
            values.forEach((val, j) => {
              const cell = row.getCell(j + 1);
              if (val instanceof Date) {
                cell.value = val;
                cell.numFmt = "mm/dd/yyyy";
              } else {
                cell.value = val as string;
              }
            });
            row.height = desiredRowHeight;
            // ExcelJS row.commit() method not exposed in types
            const rowInternal = row as unknown as ExcelJSRowInternal;
            if (typeof rowInternal.commit === "function") {
              rowInternal.commit();
            }
          });

          const lastDataRow = allRows.length
            ? firstDataRow + allRows.length - 1
            : firstDataRow - 1;
          // ExcelJS actualRowCount property not exposed in types
          const worksheetInternal =
            worksheet as unknown as ExcelJSWorksheetInternal;
          const currentRowCount: number =
            worksheetInternal.actualRowCount ||
            worksheet.rowCount ||
            lastDataRow;
          const rowsToDelete = Math.max(0, currentRowCount - lastDataRow);
          if (rowsToDelete > 0) {
            worksheet.spliceRows(lastDataRow + 1, rowsToDelete);
          }

          if (!allRows.length) {
            const rc: number =
              worksheetInternal.actualRowCount || worksheet.rowCount || 3;
            if (rc >= firstDataRow) {
              worksheet.spliceRows(firstDataRow, rc - (firstDataRow - 1));
            }
          }

          const buffer = await workbook.xlsx.writeBuffer();
          const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const a = document.createElement("a");
          const objectUrl = URL.createObjectURL(blob);
          a.href = objectUrl;
          a.download = `requests-${new Date().toISOString()}.xlsx`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(objectUrl);
        } finally {
          setIsExporting(false);
        }
      },
      btnContainerClasses: "box-border min-w-[8rem]",
    },
    {
      label: "Add New",
      icon: "PlusIcon",
      onClick: () => navigate("/admin/requests/new"),
      btnContainerClasses: "box-border min-w-[8rem]",
    },
  ];

  return { openFilters, setOpenFilters, actionButtonsConfig, filterButton };
};
